import React, { useState } from 'react';
import api from '../../../services/api';

function PartnerSettingsTab({ partner, onUpdate }) {
  const [formData, setFormData] = useState({
    displayName: partner.displayName || '',
    partnerKey: partner.partnerKey || '',
    jwtAlgorithm: partner.jwtAlgorithm || 'HS256',
    jwtSecret: partner.jwtSecret || '',
    isActive: partner.isActive !== undefined ? partner.isActive : true,
    // Plugin settings
    colorScheme: partner.pluginSettings?.colorScheme || 'blue-purple',
    flowAlias: partner.pluginSettings?.flowAlias || 'Guides',
    positionRight: partner.pluginSettings?.position?.right || '20px',
    positionBottom: partner.pluginSettings?.position?.bottom || '20px',
    positionLeft: partner.pluginSettings?.position?.left || '',
    positionTop: partner.pluginSettings?.position?.top || '',
    enableFlows: partner.pluginSettings?.enableFlows !== undefined ? partner.pluginSettings.enableFlows : true,
    enableReferrals: partner.pluginSettings?.enableReferrals !== undefined ? partner.pluginSettings.enableReferrals : true,
    enableRewards: partner.pluginSettings?.enableRewards !== undefined ? partner.pluginSettings.enableRewards : true,
    enableSupport: partner.pluginSettings?.enableSupport !== undefined ? partner.pluginSettings.enableSupport : true,
    supportEmail: partner.pluginSettings?.supportEmail || '',
    // Overlay settings
    overlayEnabled: partner.pluginSettings?.overlay?.enabled !== undefined ? partner.pluginSettings.overlay.enabled : false,
    overlayMode: partner.pluginSettings?.overlay?.mode || 'none',
    overlayRequirePassword: partner.pluginSettings?.overlay?.requirePassword !== undefined ? partner.pluginSettings.overlay.requirePassword : false,
    overlayPassword: partner.pluginSettings?.overlay?.password || '',
    overlayShowTeaser: partner.pluginSettings?.overlay?.showTeaser !== undefined ? partner.pluginSettings.overlay.showTeaser : true,
    overlayTeaserMessage: partner.pluginSettings?.overlay?.teaserMessage || 'Welcome! Enter your access code to unlock exclusive features.'
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setMessage(null);

      // Prepare update payload
      const updateData = {
        displayName: formData.displayName,
        jwtAlgorithm: formData.jwtAlgorithm,
        isActive: formData.isActive,
        pluginSettings: {
          colorScheme: formData.colorScheme,
          flowAlias: formData.flowAlias,
          position: {
            right: formData.positionRight || null,
            bottom: formData.positionBottom || null,
            left: formData.positionLeft || null,
            top: formData.positionTop || null
          },
          enableFlows: formData.enableFlows,
          enableReferrals: formData.enableReferrals,
          enableRewards: formData.enableRewards,
          enableSupport: formData.enableSupport,
          supportEmail: formData.supportEmail,
          overlay: {
            enabled: formData.overlayEnabled,
            mode: formData.overlayMode,
            requirePassword: formData.overlayRequirePassword,
            password: formData.overlayPassword,
            showTeaser: formData.overlayShowTeaser,
            teaserMessage: formData.overlayTeaserMessage
          }
        }
      };

      // Only include jwtSecret if it was changed (not empty)
      if (formData.jwtSecret && formData.jwtSecret !== partner.jwtSecret) {
        updateData.jwtSecret = formData.jwtSecret;
      }

      const response = await api.updatePartner(partner._id, updateData);
      
      setMessage({ type: 'success', text: 'Settings updated successfully' });
      
      // Notify parent component of update
      if (onUpdate && response.data.partner) {
        onUpdate(response.data.partner);
      }
    } catch (err) {
      console.error('Error updating partner:', err);
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.error || 'Failed to update settings' 
      });
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '14px'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px'
  };

  return (
    <div>
      <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '20px' }}>
        Partner Settings
      </h2>

      {message && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          borderRadius: '6px',
          backgroundColor: message.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: message.type === 'success' ? '#166534' : '#dc2626',
          border: `1px solid ${message.type === 'success' ? '#86efac' : '#fca5a5'}`
        }}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Basic Settings */}
        <div style={{
          marginBottom: '30px',
          padding: '20px',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '16px' }}>
            Basic Settings
          </h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={labelStyle}>Display Name</label>
            <input
              type="text"
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              style={inputStyle}
              required
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={labelStyle}>Partner Key (Read-only)</label>
            <input
              type="text"
              name="partnerKey"
              value={formData.partnerKey}
              style={{ ...inputStyle, backgroundColor: '#e5e7eb', cursor: 'not-allowed' }}
              disabled
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={labelStyle}>JWT Algorithm</label>
            <select
              name="jwtAlgorithm"
              value={formData.jwtAlgorithm}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="HS256">HS256</option>
              <option value="HS384">HS384</option>
              <option value="HS512">HS512</option>
            </select>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={labelStyle}>
              JWT Secret <span style={{ fontSize: '12px', color: '#6b7280' }}>(leave empty to keep current)</span>
            </label>
            <input
              type="password"
              name="jwtSecret"
              value={formData.jwtSecret}
              onChange={handleChange}
              style={inputStyle}
              placeholder="••••••••"
            />
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                style={{ marginRight: '8px' }}
              />
              <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                Active Partner
              </span>
            </label>
          </div>
        </div>

        {/* Plugin Settings */}
        <div style={{
          marginBottom: '30px',
          padding: '20px',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '16px' }}>
            Plugin Settings
          </h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={labelStyle}>Color Scheme</label>
            <select
              name="colorScheme"
              value={formData.colorScheme}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="orange-red">Orange Red</option>
              <option value="blue-purple">Blue Purple</option>
              <option value="green-teal">Green Teal</option>
            </select>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={labelStyle}>Flow Alias</label>
            <input
              type="text"
              name="flowAlias"
              value={formData.flowAlias}
              onChange={handleChange}
              style={inputStyle}
              placeholder="e.g., Guides, Journeys"
            />
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '15px',
            marginBottom: '20px'
          }}>
            <div>
              <label style={labelStyle}>Position Right</label>
              <input
                type="text"
                name="positionRight"
                value={formData.positionRight}
                onChange={handleChange}
                style={inputStyle}
                placeholder="e.g., 20px"
              />
            </div>
            <div>
              <label style={labelStyle}>Position Bottom</label>
              <input
                type="text"
                name="positionBottom"
                value={formData.positionBottom}
                onChange={handleChange}
                style={inputStyle}
                placeholder="e.g., 20px"
              />
            </div>
            <div>
              <label style={labelStyle}>Position Left (optional)</label>
              <input
                type="text"
                name="positionLeft"
                value={formData.positionLeft}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Leave empty"
              />
            </div>
            <div>
              <label style={labelStyle}>Position Top (optional)</label>
              <input
                type="text"
                name="positionTop"
                value={formData.positionTop}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Leave empty"
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="enableFlows"
                checked={formData.enableFlows}
                onChange={handleChange}
                style={{ marginRight: '8px' }}
              />
              <span style={{ fontSize: '14px', color: '#374151' }}>Enable Flows</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="enableReferrals"
                checked={formData.enableReferrals}
                onChange={handleChange}
                style={{ marginRight: '8px' }}
              />
              <span style={{ fontSize: '14px', color: '#374151' }}>Enable Referrals</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="enableRewards"
                checked={formData.enableRewards}
                onChange={handleChange}
                style={{ marginRight: '8px' }}
              />
              <span style={{ fontSize: '14px', color: '#374151' }}>Enable Rewards</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="enableSupport"
                checked={formData.enableSupport}
                onChange={handleChange}
                style={{ marginRight: '8px' }}
              />
              <span style={{ fontSize: '14px', color: '#374151' }}>Enable Support</span>
            </label>
          </div>

          {formData.enableSupport && (
            <div style={{ marginTop: '15px' }}>
              <label style={labelStyle}>Support Email</label>
              <input
                type="email"
                name="supportEmail"
                value={formData.supportEmail}
                onChange={handleChange}
                style={inputStyle}
                placeholder="support@example.com"
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                Used for the "Email Support" mailto link in the plugin's Support tab.
              </p>
            </div>
          )}
        </div>

        {/* Overlay Settings */}
        <div style={{
          marginBottom: '30px',
          padding: '20px',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '16px' }}>
            Overlay / Welcome Screen Settings
          </h3>

          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '20px' }}>
            <input
              type="checkbox"
              name="overlayEnabled"
              checked={formData.overlayEnabled}
              onChange={handleChange}
              style={{ marginRight: '8px' }}
            />
            <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>Enable Overlay/Welcome Screen</span>
          </label>

          {formData.overlayEnabled && (
            <>
              <div style={{ marginBottom: '15px' }}>
                <label style={labelStyle}>Overlay Mode</label>
                <select
                  name="overlayMode"
                  value={formData.overlayMode}
                  onChange={handleChange}
                  style={inputStyle}
                >
                  <option value="none">None (Disabled)</option>
                  <option value="welcome">Welcome Message</option>
                  <option value="code">Access Code Required</option>
                </select>
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  Choose how the overlay behaves: none (no overlay), welcome (simple message), or code (requires password)
                </p>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '15px' }}>
                <input
                  type="checkbox"
                  name="overlayRequirePassword"
                  checked={formData.overlayRequirePassword}
                  onChange={handleChange}
                  style={{ marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px', color: '#374151' }}>Require Access Code</span>
              </label>

              {formData.overlayRequirePassword && (
                <div style={{ marginBottom: '15px' }}>
                  <label style={labelStyle}>Access Code</label>
                  <input
                    type="text"
                    name="overlayPassword"
                    value={formData.overlayPassword}
                    onChange={handleChange}
                    style={inputStyle}
                    placeholder="Enter access code"
                  />
                  <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    Users must enter this code to access the site
                  </p>
                </div>
              )}

              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '15px' }}>
                <input
                  type="checkbox"
                  name="overlayShowTeaser"
                  checked={formData.overlayShowTeaser}
                  onChange={handleChange}
                  style={{ marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px', color: '#374151' }}>Show Teaser Message</span>
              </label>

              <div style={{ marginBottom: '15px' }}>
                <label style={labelStyle}>Teaser/Welcome Message</label>
                <textarea
                  name="overlayTeaserMessage"
                  value={formData.overlayTeaserMessage}
                  onChange={handleChange}
                  style={{
                    ...inputStyle,
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                  placeholder="Enter the message to display on the overlay"
                />
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  This message will be displayed on the welcome/teaser screen
                </p>
              </div>
            </>
          )}
        </div>

        {/* Save Button */}
        <button
          type="submit"
          disabled={saving}
          style={{
            padding: '10px 24px',
            backgroundColor: saving ? '#9ca3af' : '#4f46e5',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: saving ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s'
          }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </form>
    </div>
  );
}

export default PartnerSettingsTab;
